import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { patternBg } from "@/lib/patternBg";
import { LeadTimeNote } from "@/components/shop/LeadTimeNote";
import { AgendaNote } from "@/components/agenda/AgendaNote";
import { useOrderGate } from "@/lib/agendaStore";
import { useCart } from "@/context/CartContext";
import { ShareCart } from "@/components/shop/ShareCart";
import { CartLine } from "@/components/shop/CartLine";
import { formatPrice } from "@/lib/format";

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, hasUnpriced, hasSoldOut, hasPhraseProblem, patterns } = useCart();
  const { blocked } = useOrderGate(items.map((i) => i.slug));
  const canCheckout = !blocked && !hasSoldOut && !hasPhraseProblem && !hasUnpriced;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="cart-drawer" className="flex w-full flex-col border-0 bg-ivory p-0 sm:max-w-md [&>button]:hidden">
        <div className="relative flex shrink-0 items-center justify-between bg-[#D9BDB6] px-6 py-4" style={patternBg("vichy-rosa-claro", "217,189,182", 0.8, 240)}>
          <div>
            <SheetTitle className="font-[Pacifico] text-2xl font-normal leading-none text-ink">O teu carrinho</SheetTitle>
            <SheetDescription className="mt-1 text-xs text-ink/70">{items.length === 0 ? "Ainda sem peças" : `${items.length} ${items.length === 1 ? "peça" : "peças"} escolhida${items.length === 1 ? "" : "s"}`}</SheetDescription>
          </div>
          <SheetClose className="grid h-11 w-11 place-items-center rounded-full text-ink hover:bg-ivory/50" aria-label="Fechar carrinho"><X className="h-5 w-5" /></SheetClose>
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-[2px] bg-[repeating-linear-gradient(90deg,rgba(117,67,75,.42)_0_7px,transparent_7px_13px)]" />
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <div className="py-16 text-center" data-testid="cart-drawer-empty">
              <p className="font-serif text-2xl">Ainda não escolheste nada.</p>
              <Link to="/loja" onClick={() => setOpen(false)} className="bb-btn-ghost mt-6">Explorar a loja</Link>
            </div>
          ) : (
            items.map((i) => <CartLine key={i.key} item={i} compact />)
          )}
        </div>
        {items.length > 0 && (
          <div className="shrink-0 space-y-3 border-t-2 border-dashed border-bordeaux/25 bg-ivory px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-baseline justify-between text-sm">
              <span>Subtotal</span>
              <span data-testid="cart-drawer-subtotal" className="font-semibold">{formatPrice(subtotal)}{hasUnpriced && " + a confirmar"}</span>
            </div>
            <LeadTimeNote variant="short" className="!bg-rose-soft/70" patterns={patterns} />
            <AgendaNote slugs={items.map((i) => i.slug)} />
            {canCheckout ? (
              <>
                <Link to="/finalizar" data-testid="cart-drawer-checkout" onClick={() => setOpen(false)} className="bb-btn-primary w-full">Finalizar compra</Link>
                <div className="flex items-center justify-center gap-5 text-sm">
                  <Link to="/carrinho" data-testid="cart-drawer-view-cart" onClick={() => setOpen(false)} className="inline-flex min-h-[44px] items-center text-bordeaux underline underline-offset-2">Ver carrinho</Link>
                  <button type="button" onClick={() => setOpen(false)} data-testid="cart-drawer-continue" className="inline-flex min-h-[44px] items-center text-taupe-strong underline underline-offset-2">Continuar a comprar</button>
                </div>
              </>
            ) : (
              <Link to="/carrinho" data-testid="cart-drawer-view-cart" onClick={() => setOpen(false)} className="bb-btn-primary w-full">Ver carrinho</Link>
            )}
            <ShareCart compact items={items} subtotal={subtotal} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
